<template>
  <LayoutLogin>
    <template v-slot:content>
      <LayoutLoginCard>
        <Logo class="access__centered-element" type="big" />
        <p class="my-4 text-body-1 text-center">
          {{ $t('auth.access.saml_token_verification') }}
        </p>

        <LoaderSpinner v-if="isLoading" />
      </LayoutLoginCard>

      <BrowserConfirmationDialog
        :show="isConfirmationDialogVisible"
        @continue="signIn"
      />
    </template>
  </LayoutLogin>
</template>

<script>
import UAParser from 'ua-parser-js'
import qs from 'qs'
import { mapActions } from 'vuex'
import * as Sentry from '@sentry/browser'

import config from '@/config'
import { isOfficeEnvironment, sendDataToOffice } from '@/lib/office'

import mixin from '@/mixins/mixin.vue'
import authMixin from '@/mixins/authMixin.vue'

import LayoutLogin from '@/components/Core/Layout/LayoutLogin'
import LayoutLoginCard from '@/containers/Access/components/LayoutLoginCard.vue'
import LoaderSpinner from '@/components/utils/LoaderSpinner.vue'
import Logo from '@/components/Core/Logo/Logo.vue'
import BrowserConfirmationDialog from './BrowserConfirmationDialog.vue'

const MOBILE_DEVICE_TYPES = ['mobile', 'tablet'] // These types were defined based on the posible values returned by the "getDevice" method of the UAParser library. See https://github.com/faisalman/ua-parser-js#methods for more info.

export default {
  name: 'SAMLRedirectView',

  mixins: [authMixin, mixin],

  components: {
    LayoutLogin,
    LayoutLoginCard,
    Logo,
    LoaderSpinner,
    BrowserConfirmationDialog,
  },

  data() {
    return {
      isLoading: true,
      isConfirmationDialogVisible: false,
    }
  },

  mounted() {
    // This timeout allows to display an info loading message and gives Sentry the time to be initialized to report errors properly.
    setTimeout(() => {
      if (this.isInMobileDevice() && !isOfficeEnvironment()) {
        this.redirectToMobileApp()
        return
      }

      this.signIn()
    }, 2000)
  },

  methods: {
    ...mapActions({
      getMe: 'getMe',
      setToken: 'setToken',
    }),

    // It means that users won't be able to login using SAML when the Web app is opened on a mobile browsers. It's a weird scenario but we should be clear.
    isInMobileDevice() {
      const parser = new UAParser()
      const device = parser.getDevice()
      return MOBILE_DEVICE_TYPES.includes(device?.type)
    },

    redirectToMobileApp() {
      const error = this.getErrorFromUrl()
      if (error) return this.handleErrorInURL()

      const token = this.getTokenFromUrl()
      if (!token) return this.handleMissingToken()

      window.location.replace(
        `${config.mobileAppId}://${location.host}/saml/callback?accessToken=${token}`
      )

      setTimeout(() => {
        this.isConfirmationDialogVisible = true
      }, 5000)
    },

    async signIn() {
      const error = this.getErrorFromUrl()
      if (error) return this.handleErrorInURL()

      const token = this.getTokenFromUrl()
      if (!token) return this.handleMissingToken()

      this.setToken(token)

      const me = await this.getMe()

      if (isOfficeEnvironment()) {
        return sendDataToOffice({
          account: me,
          accessToken: token,
        })
      }

      await this.setLoginData({
        account: me,
        accessToken: token,
        userId: me._id,
      })

      const redirect = this.$route.query?.redirect ?? sessionStorage.getItem('redirect')
      sessionStorage.removeItem('redirect')
      return this.navigateTo(redirect ?? '/home')
    },

    getTokenFromUrl() {
      const queryParams = qs.parse(window.location.search.replace('?', ''))
      if (queryParams.access_token) {
        return queryParams.access_token
      }

      return false
    },

    getErrorFromUrl() {
      const queryParams = qs.parse(window.location.search.replace('?', ''))

      if (queryParams.error) {
        try {
          return JSON.parse(queryParams.error)
        } catch {
          Sentry.captureException(
            `SAML login error: Error parsing exception: ${queryParams.error}`
          )

          return queryParams.error
        }
      }

      return false
    },

    handleErrorInURL() {
      const token = this.getTokenFromUrl()
      const error = this.getErrorFromUrl()

      Sentry.captureException(`SAML login error: ${error}\nToken: ${token}`)
      this.navigateTo('/access')
      this.showError(`${error}`)
    },

    handleMissingToken() {
      Sentry.captureException(
        'SAML login error: No token was obtained from API.'
      )
      this.navigateTo('/access')
      this.showError(this.$t('auth.access.saml_missing_token_error'))
    },
  },
}
</script>

<style lang="scss" scoped>
.access__centered-element {
  text-align: center;
  margin: 0 auto 24px;
}

.loader-container {
  height: auto;
  padding-top: 70px;
  padding-bottom: 20px;
}
</style>
